import { useState } from 'react';

import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  useClassifierConfigDetail,
  useCreateClassifierConfig,
  useUpdateClassifierConfig,
} from '@/hooks/useClassifierConfigs';
import { usePageTitle } from '@/hooks/usePageTitle';

const DEFAULT_TAXONOMY = JSON.stringify(
  {
    type: 'object',
    properties: {
      car_chosen: { type: 'boolean', description: 'Caller had a specific vehicle in mind' },
      is_change_booking: {
        type: 'boolean',
        description: 'Caller wants to change a showroom booking',
      },
      buying_intent_level: {
        type: 'integer',
        description: 'Buying intent score 1-5',
      },
    },
    required: ['car_chosen', 'is_change_booking', 'buying_intent_level'],
  },
  null,
  2,
);

const DEFAULT_PROMPT =
  'You are a call classifier. Read the call transcript and produce flat dashboard labels per the schema. Booleans should reflect what genuinely happened in the call.';

export const ClassifierConfigEditPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = !routeId || routeId === 'new';

  usePageTitle(isNew ? 'New Classifier Config' : `Classifier Config`);

  const { data: detail, isLoading } = useClassifierConfigDetail(isNew ? undefined : routeId);
  const createMutation = useCreateClassifierConfig();
  const updateMutation = useUpdateClassifierConfig();

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [modelName, setModelName] = useState('gemini-2.5-flash');
  const [temperature, setTemperature] = useState('0.0');
  const [taxonomyText, setTaxonomyText] = useState(DEFAULT_TAXONOMY);
  const [taxonomyVersion, setTaxonomyVersion] = useState('v1');
  const [isDefault, setIsDefault] = useState(false);
  const [injectCurrentDate, setInjectCurrentDate] = useState(false);
  const [highIntentEmails, setHighIntentEmails] = useState('');
  const [taxonomyError, setTaxonomyError] = useState<string | null>(null);

  // Sync local form state from server-loaded detail. React-recommended pattern
  // for "adjust state when a prop/derived value changes" — store prevDetail in
  // state and update local fields during render when detail changes. Avoids
  // the cascading-render anti-pattern of calling setState in useEffect.
  const [prevDetail, setPrevDetail] = useState(detail);
  if (detail !== prevDetail) {
    setPrevDetail(detail);
    if (detail) {
      setName(detail.name);
      setPrompt(detail.prompt);
      setModelName(detail.model_name);
      setTemperature(String(detail.temperature));
      setTaxonomyText(JSON.stringify(detail.taxonomy, null, 2));
      setTaxonomyVersion(detail.taxonomy_version);
      setIsDefault(detail.is_default);
      setInjectCurrentDate(detail.inject_current_date ?? false);
      setHighIntentEmails(detail.high_intent_emails.join(', '));
    }
  }

  const parseTaxonomy = (): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(taxonomyText);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setTaxonomyError('Taxonomy must be a JSON object');
        return null;
      }
      setTaxonomyError(null);
      return parsed as Record<string, unknown>;
    } catch (err) {
      setTaxonomyError(err instanceof Error ? err.message : 'Invalid JSON');
      return null;
    }
  };

  const handleSave = async () => {
    const taxonomy = parseTaxonomy();
    if (!taxonomy) return;
    if (!name.trim()) {
      toast({ variant: 'destructive', title: 'Missing name', description: 'Add a config name.' });
      return;
    }
    if (!taxonomyVersion.trim()) {
      toast({
        variant: 'destructive',
        title: 'Missing taxonomy version',
        description: 'Bump this when the schema changes — drives backfill detection.',
      });
      return;
    }

    const payload = {
      name,
      prompt,
      model_name: modelName,
      temperature: Number(temperature) || 0,
      taxonomy,
      taxonomy_version: taxonomyVersion,
      is_default: isDefault,
      inject_current_date: injectCurrentDate,
      high_intent_emails: highIntentEmails
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean),
    };

    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        toast({
          variant: 'success',
          title: 'Created',
          description: `Config '${created.name}' created.`,
        });
      } else {
        await updateMutation.mutateAsync({ id: routeId, data: payload });
        toast({ variant: 'success', title: 'Saved', description: `Config '${name}' updated.` });
      }
      navigate('/classifier-configs');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast({ variant: 'destructive', title: 'Save failed', description: msg });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/classifier-configs')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'New classifier config' : `Edit: ${detail?.name}`}</CardTitle>
          <CardDescription>
            Defines the prompt, model, and taxonomy (JSON Schema) for the ClassifierAgent. Booleans
            become labels; everything else lands in data. Bump taxonomy_version when the schema
            changes — drives the per-DB backfill detection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="V12 main classifier"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Model</label>
              <Input
                value={modelName}
                onChange={(e) => setModelName(e.target.value)}
                placeholder="gemini-2.5-flash"
                className="font-mono text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Temperature</label>
              <Input
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                type="number"
                step="0.1"
                min="0"
                max="2"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Taxonomy version</label>
            <Input
              value={taxonomyVersion}
              onChange={(e) => setTaxonomyVersion(e.target.value)}
              placeholder="v1"
              className="font-mono text-xs"
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
            <span className="text-sm">{isDefault ? 'Default for this DB' : 'Not the default'}</span>
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Switch checked={injectCurrentDate} onCheckedChange={setInjectCurrentDate} />
              <span className="text-sm">
                {injectCurrentDate ? 'Inject current date' : "Don't inject current date"}
              </span>
            </div>
            <p className="text-muted-foreground text-xs">
              When on, a &quot;current date&quot; block (anchored on the call&apos;s date) is
              prepended to the prompt so the classifier can resolve relative dates like &quot;next
              Tuesday&quot; into absolute YYYY-MM-DD values.
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">High-intent emails (comma-separated)</label>
            <Input
              value={highIntentEmails}
              onChange={(e) => setHighIntentEmails(e.target.value)}
              placeholder="alerts@example.com, ops@example.com"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={6}
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Taxonomy (JSON Schema)</label>
            <textarea
              value={taxonomyText}
              onChange={(e) => setTaxonomyText(e.target.value)}
              rows={20}
              className="border-input bg-background w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
            {taxonomyError && <p className="text-destructive text-xs">{taxonomyError}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => navigate('/classifier-configs')}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
